/**
 * Why an async copy did not finish successfully.
 *
 * Deliberately NOT a GraphRequestError: a copy's failure never arrives as an HTTP status. The
 * `POST .../copy` returns 202 Accepted whatever happens afterwards, and the real outcome — including
 * a destination name collision — is reported by the monitor resource as a `status: 'failed'` body
 * carrying an error `code`. So callers classify on `code`, not on `status`, which is the one place
 * in this service where that is true.
 */
export type CopyFailureReason =
  /** The monitor reported `status: 'failed'`; `code` carries Graph's own error code. */
  | 'failed'
  /** The poll deadline expired. NOT a failure — SharePoint is very likely still copying. */
  | 'timeout'
  /** The poll was abandoned (the file manager unmounted). The copy itself continues server-side. */
  | 'abandoned';

/** Graph's error code for a destination name collision, under conflictBehavior 'fail'. */
export const COPY_NAME_CONFLICT_CODE = 'nameAlreadyExists';

export class CopyMonitorError extends Error {
  constructor(
    public readonly reason: CopyFailureReason,
    /** Graph's error code, when the monitor supplied one. */
    public readonly code?: string,
    message?: string,
  ) {
    super(message ?? `SharePoint copy ${reason}${code ? ` (${code})` : ''}`);
    this.name = 'CopyMonitorError';
  }

  get isNameConflict(): boolean {
    return this.code === COPY_NAME_CONFLICT_CODE;
  }
}
